import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeFilled, DollarCircleFilled, FileFilled, PlusOutlined, MessageFilled, SafetyCertificateOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Drawer, Modal, Button, Collapse, message, Result, Badge, Alert, Form, InputNumber, DatePicker, Input } from 'antd';
import InvoiceProcessor from './InvoiceProcessor';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getDatabase, ref as dbRef, set, push, onValue } from 'firebase/database';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import Lottie from 'lottie-react';
import successAnimation from './lottie/success.json';

import { motion } from 'framer-motion';
import './MobileMenu.css';

const { Panel } = Collapse;

const MobileMenu = () => {
    const location = useLocation();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [isAddingNewInvoice, setIsAddingNewInvoice] = useState(false);
    const [showTypeSelector, setShowTypeSelector] = useState(true);
    const [showKsefForm, setShowKsefForm] = useState(false);
    const [ksefLoading, setKsefLoading] = useState(false);
    const [ksefForm] = Form.useForm();
    const [driverId, setDriverId] = useState(null);
    const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        const fetchDriverId = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const db = getFirestore();
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                if (userDoc.exists()) {
                    setDriverId(userDoc.data().driverId);
                }
            }
        };

        fetchDriverId();
    }, []);

    useEffect(() => {
        const auth = getAuth();
        const user = auth.currentUser;

        if (user) {
            const db = getDatabase();
            const chatsRef = dbRef(db, 'chats');

            onValue(chatsRef, (snapshot) => {
                let count = 0;
                snapshot.forEach((chatSnap) => {
                    const chat = chatSnap.val();
                    const chatId = chatSnap.key;

                    if (chat.creatorId === user.uid) {
                        const messagesRef = dbRef(db, `chats/${chatId}/messages`);

                        onValue(messagesRef, (messagesSnapshot) => {
                            messagesSnapshot.forEach((messageSnap) => {
                                const message = messageSnap.val();
                                if (!message.isRead && message.sender !== user.email) {
                                    count += 1;
                                }
                            });
                            setUnreadCount(count);
                        });
                    }
                });
            });
        }
    }, []);

    const showModal = (event) => {
        event.preventDefault();
        setIsModalVisible(true);
        setIsAddingNewInvoice(false);
        setShowTypeSelector(true);
        setShowKsefForm(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setIsAddingNewInvoice(false);
        setShowTypeSelector(true);
        setShowKsefForm(false);
        setInvoices([]);
        setIsSaveSuccessful(false);
        ksefForm.resetFields();
    };

    const handleKsefSubmit = async (values) => {
        if (!driverId) {
            message.error('Nie znaleziono ID kierowcy');
            return;
        }
        setKsefLoading(true);
        try {
            const db = getDatabase();
            const invoiceData = {
                timestamp: Date.now(),
                invoiceDate: values.date.format('YYYY-MM-DD'),
                grossAmount: values.amount,
                vehicleNumber: values.vehicleNumber || '',
                status: 'W trakcie weryfikacji',
                addedBy: 'driver',
                source: 'ksef_driver',
                type: 'KSeF',
                uploadDate: dayjs().format('YYYY-MM-DD HH:mm:ss'),
            };
            const invoicesRef = dbRef(db, `drivers/${driverId}/invoices`);
            const newRef = push(invoicesRef);
            await set(newRef, invoiceData);
            message.success('Faktura KSeF dodana pomyślnie');
            ksefForm.resetFields();
            setIsSaveSuccessful(true);
            setShowKsefForm(false);
        } catch (error) {
            message.error('Błąd: ' + error.message);
        }
        setKsefLoading(false);
    };

    const handleProcessComplete = (data) => {
        // Упрощенная обработка - только базовые поля от Firebase
        const invoiceData = {
            documentId: data.id || uuidv4(),
            filename: data.filename || 'Загруженная факtura',
            fileURL: data.fileURL,
            status: data.status || 'W trakcie weryfikacji',
            timestamp: data.timestamp || Date.now()
        };

        setInvoices(prevInvoices => [...prevInvoices, invoiceData]);
        setIsAddingNewInvoice(false);
    };

    const saveAllInvoices = async () => {
        if (!driverId) {
            message.error('Nie udało się uzyskać driverId. Spróbuj ponownie później.');
            return;
        }

        const db = getDatabase();
        const promises = invoices.map((invoice) => {
            const invoiceRef = dbRef(db, `drivers/${driverId}/invoices/${invoice.documentId}`);
            return set(invoiceRef, invoice);
        });

        try {
            await Promise.all(promises);
            message.success('Wszystkie faktury zostały pomyślnie zapisane w bazie danych!');
            setIsSaveSuccessful(true);
        } catch (error) {
            message.error('Błąd podczas zapisywania faktur w bazie danych.');
        }
    };

    const addMoreInvoices = () => {
        setIsAddingNewInvoice(true);
    };

    return (
        <div className="mobile-menu">
            <Link to="/" className={location.pathname === '/' ? 'active' : ''}>
                <HomeFilled />
            </Link>

            <Link to="/rozliczenia" className={location.pathname === '/rozliczenia' ? 'active' : ''}>
                <DollarCircleFilled />
            </Link>

            <Link to="/samochod" className={location.pathname === '/samochod' ? 'active' : ''} onClick={showModal}>
                <PlusOutlined />
            </Link>

            <Link to="/faktury" className={location.pathname === '/faktury' ? 'active' : ''}>
                <FileFilled />
            </Link>
            <Link to="/pomoc" className={location.pathname === '/pomoc' ? 'active' : ''}>
                <Badge
                    count={unreadCount}
                    overflowCount={99}
                    offset={[0, 0]}
                    style={{ backgroundColor: '#ff4d4f', color: '#fff', boxShadow: 'none' }}
                >
                    <MessageFilled style={{ fontSize: '24px', color: location.pathname === '/pomoc' ? '#000' : '#8c8c8c' }} />
                </Badge>
            </Link>

            <Drawer
                title="Nowa Faktura"
                placement="bottom"
                height="auto"
                open={isModalVisible}
                onClose={handleCancel}
                styles={{ body: { paddingBottom: 80, maxHeight: '80vh', overflowY: 'auto' } }}
                extra={
                    invoices.length > 0 && !isSaveSuccessful ? (
                        <Button type="primary" size="small" onClick={saveAllInvoices}>
                            Wyślij
                        </Button>
                    ) : null
                }
            >
                {isSaveSuccessful ? (
                    <Result
                        status="success"
                        title="Wszystkie faktury zostały pomyślnie zapisane!"
                        icon={<Lottie animationData={successAnimation} loop={false} />}
                        extra={[
                            <Button type="primary" key="close" onClick={handleCancel}>
                                Zamknij
                            </Button>,
                        ]}
                    />
                ) : showTypeSelector && invoices.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <Button
                            type="primary"
                            size="large"
                            icon={<FileFilled />}
                            onClick={() => { setShowTypeSelector(false); setIsAddingNewInvoice(true); }}
                            style={{ width: '100%', height: 56 }}
                        >
                            Dodaj fakturę (zdjęcie/plik)
                        </Button>
                        <Button
                            size="large"
                            icon={<SafetyCertificateOutlined />}
                            onClick={() => { setShowTypeSelector(false); setShowKsefForm(true); }}
                            style={{ width: '100%', height: 56, borderColor: '#1890ff', color: '#1890ff' }}
                        >
                            Faktura wystawiona w KSeF
                        </Button>
                    </div>
                ) : showKsefForm ? (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                    >
                        <motion.h3
                            style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}
                            initial={{ x: -20, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1, duration: 0.3 }}
                        >
                            <SafetyCertificateOutlined style={{ marginRight: 8, color: '#1890ff', fontSize: 20 }} />
                            Faktura wystawiona w KSeF
                        </motion.h3>
                        <Form form={ksefForm} layout="vertical" onFinish={handleKsefSubmit}>
                            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.3 }}>
                                <Form.Item name="date" label="Data tankowania / faktury" rules={[{ required: true, message: 'Wybierz datę' }]}>
                                    <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="Wybierz datę" />
                                </Form.Item>
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.3 }}>
                                <Form.Item name="amount" label="Kwota (brutto)" rules={[{ required: true, message: 'Wpisz kwotę' }]}>
                                    <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} placeholder="0.00" addonAfter="zł" />
                                </Form.Item>
                            </motion.div>
                            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.3 }}>
                                <Form.Item name="vehicleNumber" label="Nr rejestracyjny" rules={[{ required: true, message: 'Wpisz numer rejestracyjny' }]}>
                                    <Input placeholder="np. WA 12345" />
                                </Form.Item>
                            </motion.div>
                            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.45, duration: 0.3 }}>
                                <Form.Item>
                                    <Button type="primary" htmlType="submit" loading={ksefLoading} block size="large" icon={<SafetyCertificateOutlined />}>
                                        Dodaj fakturę KSeF
                                    </Button>
                                </Form.Item>
                            </motion.div>
                        </Form>
                    </motion.div>
                ) : (
                    <>
                        {invoices.length > 0 && (
                            <div style={{ marginBottom: 20 }}>
                                <h3>Lista Faktur:</h3>
                                <Alert
                                    message="Faktury zostały załadowane. Aby wysłać do Partnera, naciśnij przycisk 'Wyślij'."
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 20, marginBottom: 20 }}
                                />
                                <Collapse accordion>
                                    {invoices.map((invoice, index) => (
                                        <Panel
                                            header={`${invoice.filename} - ${invoice.status}`}
                                            key={invoice.documentId || index}
                                        >
                                            <div style={{ padding: '10px 0' }}>
                                                <p><strong>Nazwa pliku:</strong> {invoice.filename}</p>
                                                <p><strong>Status:</strong> {invoice.status}</p>
                                                <p><strong>Data przesłania:</strong> {new Date(invoice.timestamp).toLocaleString('pl-PL')}</p>
                                                <div style={{ marginTop: 15 }}>
                                                    <a
                                                        href={invoice.fileURL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            display: 'inline-block',
                                                            padding: '8px 16px',
                                                            backgroundColor: '#1890ff',
                                                            color: 'white',
                                                            textDecoration: 'none',
                                                            borderRadius: '4px'
                                                        }}
                                                    >
                                                        Pobierz fakturę
                                                    </a>
                                                </div>
                                            </div>
                                        </Panel>
                                    ))}
                                </Collapse>
                            </div>
                        )}

                        {isAddingNewInvoice && (
                            <InvoiceProcessor onProcessComplete={handleProcessComplete} />
                        )}

                        {!isAddingNewInvoice && invoices.length > 0 && (
                            <Button type="primary" onClick={addMoreInvoices} style={{ width: '100%', marginBottom: 10 }}>
                                Dodaj więcej faktur
                            </Button>
                        )}
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default MobileMenu;